<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';

const props = defineProps({
  modelValue: { type: Object, required: true },
});

const emit = defineEmits(['update:modelValue', 'remove']);

const { t } = useI18n();

const isUploading = ref(false);

const update = changes => {
  emit('update:modelValue', { ...props.modelValue, ...changes });
};

const onSelectImage = async event => {
  const file = event.target.files[0];
  event.target.value = '';
  if (!file) return;

  isUploading.value = true;
  try {
    const { proxyUrl } = await uploadFile(file);
    update({ media_url: proxyUrl });
  } catch (error) {
    useAlert(t('AUTOMATION.INTERACTIVE_MESSAGE.CARD_IMAGE_UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const removeImage = () => {
  update({ media_url: '' });
};

const addAction = () => {
  update({
    actions: [
      ...props.modelValue.actions,
      { type: 'postback', text: '', payload: '', uri: '' },
    ],
  });
};

const removeAction = index => {
  const actions = props.modelValue.actions.filter((_, i) => i !== index);
  update({ actions });
};

const updateAction = (index, changes) => {
  const actions = props.modelValue.actions.map((action, i) =>
    i === index ? { ...action, ...changes } : action
  );
  update({ actions });
};
</script>

<template>
  <div
    class="flex gap-3 p-3 rounded-lg border border-n-weak bg-n-slate-2 dark:bg-n-solid-2"
  >
    <label
      class="group relative flex items-center justify-center shrink-0 size-16 rounded-lg bg-n-alpha-1 text-n-slate-9 cursor-pointer overflow-hidden ring-1 ring-inset ring-n-weak transition-shadow hover:ring-n-blue-border"
    >
      <input
        type="file"
        accept="image/*"
        class="hidden"
        :disabled="isUploading"
        @change="onSelectImage"
      />
      <Spinner v-if="isUploading" />
      <template v-else-if="modelValue.media_url">
        <img
          :src="modelValue.media_url"
          class="size-full object-cover"
          alt=""
        />
        <div
          class="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity group-hover:bg-black/45 group-hover:opacity-100"
        >
          <span class="i-lucide-pencil size-4 text-white" aria-hidden="true" />
        </div>
        <NextButton
          icon="i-lucide-x"
          slate
          ghost
          xs
          class="!absolute top-0.5 right-0.5 !size-4 !min-w-0 bg-n-solid-1/90"
          @click.prevent.stop="removeImage"
        />
      </template>
      <div v-else class="flex flex-col items-center gap-0.5 text-n-slate-9">
        <span class="i-lucide-image-plus size-5" aria-hidden="true" />
      </div>
    </label>
    <div class="flex-1 min-w-0 flex flex-col gap-2">
      <Input
        :model-value="modelValue.title"
        :placeholder="
          $t('AUTOMATION.INTERACTIVE_MESSAGE.CARD_TITLE_PLACEHOLDER')
        "
        @update:model-value="value => update({ title: value })"
      />
      <Input
        :model-value="modelValue.description"
        :placeholder="
          $t('AUTOMATION.INTERACTIVE_MESSAGE.CARD_DESCRIPTION_PLACEHOLDER')
        "
        @update:model-value="value => update({ description: value })"
      />
      <div class="flex flex-col gap-2 border-t border-n-weak pt-2.5 mt-0.5">
        <div
          v-for="(action, index) in modelValue.actions"
          :key="index"
          class="flex flex-col gap-1.5 bg-n-alpha-1 rounded-md p-1.5"
        >
          <div class="flex items-center gap-1.5">
            <span
              class="text-n-slate-9 shrink-0 ml-0.5"
              :class="
                action.type === 'link'
                  ? 'i-lucide-external-link size-3.5'
                  : 'i-lucide-mouse-pointer-click size-3.5'
              "
              aria-hidden="true"
            />
            <select
              :value="action.type"
              class="flex-1 min-w-0 text-xs bg-n-solid-1 border border-n-weak rounded-md px-2 py-1.5 outline-none"
              @change="
                event => updateAction(index, { type: event.target.value })
              "
            >
              <option value="postback">
                {{ $t('AUTOMATION.INTERACTIVE_MESSAGE.ACTION_TYPE_POSTBACK') }}
              </option>
              <option value="link">
                {{ $t('AUTOMATION.INTERACTIVE_MESSAGE.ACTION_TYPE_LINK') }}
              </option>
            </select>
            <NextButton
              icon="i-lucide-x"
              slate
              ghost
              xs
              class="shrink-0"
              :disabled="modelValue.actions.length <= 1"
              @click="removeAction(index)"
            />
          </div>
          <div class="flex items-center gap-1.5">
            <Input
              class="flex-1 min-w-0"
              :model-value="action.text"
              :placeholder="
                $t('AUTOMATION.INTERACTIVE_MESSAGE.ACTION_TEXT_PLACEHOLDER')
              "
              @update:model-value="
                value => updateAction(index, { text: value })
              "
            />
            <Input
              v-if="action.type === 'link'"
              class="flex-1 min-w-0"
              :model-value="action.uri"
              :placeholder="
                $t('AUTOMATION.INTERACTIVE_MESSAGE.ACTION_URI_PLACEHOLDER')
              "
              @update:model-value="value => updateAction(index, { uri: value })"
            />
            <Input
              v-else
              class="flex-1 min-w-0"
              :model-value="action.payload"
              :placeholder="
                $t('AUTOMATION.INTERACTIVE_MESSAGE.ACTION_PAYLOAD_PLACEHOLDER')
              "
              @update:model-value="
                value => updateAction(index, { payload: value })
              "
            />
          </div>
        </div>
        <NextButton
          icon="i-lucide-plus"
          slate
          faded
          xs
          justify="center"
          :label="$t('AUTOMATION.INTERACTIVE_MESSAGE.ADD_ACTION')"
          class="w-full"
          @click="addAction"
        />
      </div>
    </div>
    <NextButton
      icon="i-lucide-trash-2"
      slate
      ghost
      xs
      class="shrink-0"
      @click="emit('remove')"
    />
  </div>
</template>
